import { Add as AddIcon, Delete as DeleteIcon } from "@mui/icons-material";
import {
    Box,
    Button,
    Card,
    CardContent,
    CardHeader,
    CircularProgress,
    FormControl,
    FormControlLabel,
    Grid,
    IconButton,
    InputLabel,
    MenuItem,
    Select,
    Switch,
    TextField,
    Typography,
} from "@mui/material";
import { Formik } from "formik";
import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import * as Yup from "yup";
import { wrapFormikHandleChange } from "../../../../../infrastructure/utils/formatters";
import FileUploader from "../../../../components/shared/uploader/FileUploader";
import EquipmentItemPicker from "../../shared/equipmentCatalog/EquipmentItemPicker";
import EquipmentPublicCodeChip from "../../shared/equipmentCatalog/EquipmentPublicCodeChip";

import {
    createSupplierPackage,
    fetchSupplierPackageDetails,
    updateSupplierPackage,
} from "../../../../../application/actions/supplierPackageActions";
import {
    selectCreateUpdateLoading,
    selectCreateUpdateSuccess,
    selectSupplierPackageData,
    selectSupplierPackageLoading,
} from "../../../../../application/selectors/supplierPackages";
import { clearCreateUpdateStatus } from "../../../../../application/slices/supplierPackages";
import BreadcrumbsComponent from "../../../../components/shared/breadcrumbs";

const validationSchema = Yup.object().shape({
  title: Yup.string().required("عنوان الزامی است"),
  plantCapacity: Yup.number().required("نوع ظرفیت الزامی است"),
  description: Yup.string(),
  priceWithEquipment: Yup.number()
    .min(0, "قیمت باید بزرگتر از صفر باشد")
    .nullable(),
  priceWithoutEquipment: Yup.number()
    .min(0, "قیمت باید بزرگتر از صفر باشد")
    .nullable(),
  deliveryFee: Yup.number()
    .min(0, "هزینه ارسال نمی‌تواند منفی باشد")
    .required("هزینه ارسال الزامی است"),
  installationDurationDays: Yup.number()
    .min(1, "مدت اجرا باید حداقل ۱ روز باشد")
    .nullable(),
  warrantyMonths: Yup.number()
    .min(0, "مدت گارانتی نمی‌تواند منفی باشد")
    .nullable(),
  status: Yup.number().required("وضعیت الزامی است"),
});

const PackageModifyPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = id !== "create";

  // Get baseUrl from global config
  const baseUrl = window.globalConfig?.API_URL || "";

  // Redux Selectors
  const packageData = useSelector(selectSupplierPackageData);
  const loading = useSelector(selectSupplierPackageLoading);
  const createUpdateLoading = useSelector(selectCreateUpdateLoading);
  const createUpdateSuccess = useSelector(selectCreateUpdateSuccess);

  // Static options (can be moved to constants file later)
  const plantCapacityOptions = [
    { value: 5, label: "5 کیلووات", name: "5 کیلووات" },
    { value: 10, label: "10 کیلووات", name: "10 کیلووات" },
    { value: 15, label: "15 کیلووات", name: "15 کیلووات" },
    { value: 20, label: "20 کیلووات", name: "20 کیلووات" },
  ];

  const equipmentTypes = [
    { value: 1, label: "پنل خورشیدی", name: "پنل خورشیدی" },
    { value: 2, label: "اینورتر", name: "اینورتر" },
    { value: 3, label: "استراکچر", name: "استراکچر" },
    { value: 4, label: "کابل", name: "کابل" },
    { value: 5, label: "کمباینر باکس", name: "کمباینر باکس" },
    { value: 6, label: "تابلو AC", name: "تابلو AC" },
    { value: 7, label: "تابلو DC", name: "تابلو DC" },
    { value: 8, label: "سیستم ارت", name: "سیستم ارت" },
    { value: 9, label: "سیستم مانیتورینگ", name: "سیستم مانیتورینگ" },
    { value: 10, label: "باتری", name: "باتری" },
    { value: 11, label: "شارژ کنترلر", name: "شارژ کنترلر" },
    { value: 99, label: "سایر", name: "سایر" },
  ];

  // Load data on mount
  useEffect(() => {
    if (isEditMode) {
      dispatch(fetchSupplierPackageDetails(id));
    }
  }, [dispatch, id, isEditMode]);

  // Handle success redirect
  useEffect(() => {
    if (createUpdateSuccess) {
      toast.success(
        isEditMode ? "پکیج با موفقیت ویرایش شد" : "پکیج با موفقیت ایجاد شد"
      );
      dispatch(clearCreateUpdateStatus());
      navigate("/SLN/Supplier/PackageManagement");
    }
  }, [createUpdateSuccess, dispatch, navigate, isEditMode]);

  const onNavigateToListPage = () => {
    navigate("/SLN/Supplier/PackageManagement");
  };

  const onSubmit = async (formValue, { setSubmitting }) => {
    try {
      // Debug: بررسی مقدار imageUrl
      console.log(
        "🔍 formValue.imageUrl:",
        formValue.imageUrl,
        typeof formValue.imageUrl
      );

      // تبدیل PlantCapacity از عدد به enum
      const plantCapacityEnum =
        formValue.plantCapacity === 5
          ? "P5KW"
          : formValue.plantCapacity === 10
          ? "P10KW"
          : formValue.plantCapacity === 15
          ? "P15KW"
          : formValue.plantCapacity === 20
          ? "P20KW"
          : "P5KW";

      // تبدیل داده‌ها به فرمت بک‌اند (camelCase)
      const payload = {
        packageId: isEditMode ? parseInt(id) : 0,
        title: formValue.title,
        plantCapacity: plantCapacityEnum,
        description: formValue.description,
        priceWithEquipment: formValue.priceWithEquipment
          ? parseFloat(formValue.priceWithEquipment)
          : null,
        priceWithoutEquipment: formValue.priceWithoutEquipment
          ? parseFloat(formValue.priceWithoutEquipment)
          : null,
        deliveryFee: parseFloat(formValue.deliveryFee) || 0,
        contractorPaysDelivery: formValue.contractorPaysDelivery,
        installationDurationDays: formValue.installationDurationDays
          ? parseInt(formValue.installationDurationDays)
          : null,
        warrantyMonths: formValue.warrantyMonths
          ? parseInt(formValue.warrantyMonths)
          : null,
        status: formValue.status,
        isActive: formValue.isActive,
        imageUrl: formValue.imageUrl || null,
        equipments: (formValue.equipments || []).map((eq) => ({
          equipmentType: eq.equipmentType,
          brand: eq.brand,
          model: eq.model,
          quantity: parseInt(eq.quantity) || 1,
          unitPrice: parseFloat(eq.unitPrice) || 0,
          totalPrice: parseFloat(eq.totalPrice) || 0,
          specifications: eq.specifications || "",
          warrantyPeriod: eq.warrantyPeriod
            ? parseInt(eq.warrantyPeriod)
            : null,
          description: eq.description || "",
          imageUrl: eq.imageUrl || "",
          equipmentItemId: eq.equipmentItemId || null,
          publicCode: eq.publicCode || "",
        })),
        features: (formValue.features || []).map((ft) => ({
          featureTitle: ft.featureTitle,
          featureValue: ft.featureValue,
          description: ft.description || "",
          displayOrder: parseInt(ft.displayOrder) || 0,
        })),
      };

      if (isEditMode) {
        await dispatch(
          updateSupplierPackage({ id, packageData: payload })
        ).unwrap();
      } else {
        await dispatch(createSupplierPackage(payload)).unwrap();
      }
    } catch (error) {
      toast.error(error || "خطایی رخ داده است.");
      setSubmitting(false);
    }
  };

  // Initial values
  const initialValues =
    isEditMode && packageData
      ? {
          title: packageData.packageTitle || packageData.title || "",
          plantCapacity:
            packageData.plantCapacity === "P5KW"
              ? 5
              : packageData.plantCapacity === "P10KW"
              ? 10
              : packageData.plantCapacity === "P15KW"
              ? 15
              : packageData.plantCapacity === "P20KW"
              ? 20
              : "",
          description: packageData.description || "",
          priceWithEquipment: packageData.priceWithEquipment || "",
          priceWithoutEquipment: packageData.priceWithoutEquipment || "",
          deliveryFee: packageData.deliveryFee || 0,
          contractorPaysDelivery: packageData.contractorPaysDelivery || false,
          installationDurationDays: packageData.installationDurationDays || "",
          warrantyMonths: packageData.warrantyMonths || "",
          status:
            packageData.status === "Draft"
              ? 1
              : packageData.status === "Active"
              ? 2
              : packageData.status === "Inactive"
              ? 3
              : packageData.status === "UnderReview"
              ? 4
              : packageData.status === "Rejected"
              ? 5
              : packageData.status === "Archived"
              ? 6
              : 1,
          isActive:
            packageData.isActive !== undefined ? packageData.isActive : true,
          imageUrl: packageData.imageUrl || "",
          equipments: (packageData.packageEquipments || []).map((eq) => ({
            equipmentType: eq.equipmentType || "",
            brand: eq.brand || "",
            model: eq.model || "",
            quantity: eq.quantity || 1,
            unitPrice: eq.unitPrice || 0,
            totalPrice: eq.totalPrice || 0,
            specifications: eq.specifications || "",
            warrantyPeriod: eq.warrantyPeriod || "",
            description: eq.description || "",
            imageUrl: eq.imageUrl || "",
            equipmentItemId: eq.equipmentItemId || null,
            publicCode: eq.publicCode || "",
            masterPicker: eq.equipmentItemId
              ? {
                  equipmentItemId: eq.equipmentItemId,
                  publicCode: eq.publicCode,
                  brandName: eq.brand,
                  modelName: eq.model,
                }
              : null,
          })),
          features: (packageData.packageFeatures || []).map((ft) => ({
            featureTitle: ft.featureTitle || "",
            featureValue: ft.featureValue || "",
            description: ft.description || "",
            displayOrder: ft.displayOrder || 0,
          })),
        }
      : {
          title: "",
          plantCapacity: "",
          description: "",
          priceWithEquipment: "",
          priceWithoutEquipment: "",
          deliveryFee: 0,
          contractorPaysDelivery: false,
          installationDurationDays: "",
          warrantyMonths: "",
          status: 1,
          isActive: true,
          imageUrl: "",
          equipments: [],
          features: [],
        };

  if (loading && isEditMode) {
    return (
      <Grid
        container
        justifyContent="center"
        alignItems="center"
        style={{ minHeight: "60vh" }}
      >
        <CircularProgress />
      </Grid>
    );
  }

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <BreadcrumbsComponent />
      </Grid>

      <Grid item xs={12}>
        <Card>
          <CardHeader
            title={
              isEditMode
                ? `ویرایش ${packageData?.title || "پکیج"}`
                : "ایجاد پکیج جدید"
            }
          />

          <CardContent>
            <Formik
              initialValues={initialValues}
              validationSchema={validationSchema}
              onSubmit={onSubmit}
              enableReinitialize
            >
              {({
                values,
                errors,
                touched,
                handleChange,
                handleBlur,
                handleSubmit,
                isSubmitting,
                setFieldValue,
              }) => {
                const onMakeFormikVariable = (name) => ({
                  name: name,
                  fullWidth: true,
                  value: values[name],
                  error: errors[name] && touched[name],
                  helperText: errors[name] && touched[name] && errors[name],
                  variant: "outlined",
                  onBlur: handleBlur,
                });

                return (
                  <form onSubmit={handleSubmit}>
                    <Grid container spacing={3}>
                      {/* اطلاعات اصلی */}
                      <Grid item xs={12}>
                        <Typography variant="h6">اطلاعات اصلی پکیج</Typography>
                      </Grid>

                      <Grid item xs={12} md={3}>
                        <TextField
                          label="عنوان پکیج"
                          onChange={wrapFormikHandleChange(handleChange)}
                          {...onMakeFormikVariable("title")}
                        />
                      </Grid>

                      <Grid item xs={12} md={3}>
                        <FormControl
                          fullWidth
                          variant="outlined"
                          error={errors.plantCapacity && touched.plantCapacity}
                        >
                          <InputLabel>ظرفیت نیروگاه</InputLabel>
                            <Select
                            name="plantCapacity"
                            label="ظرفیت نیروگاه"
                            value={values.plantCapacity}
                            onChange={wrapFormikHandleChange(handleChange)}
                            onBlur={handleBlur}
                          >
                            {plantCapacityOptions.map((option) => (
                              <MenuItem key={option.value} value={option.value}>
                                {option.label || option.name}
                              </MenuItem>
                            ))}
                          </Select>
                          {errors.plantCapacity && touched.plantCapacity && (
                            <Typography variant="caption" color="error">
                              {errors.plantCapacity}
                            </Typography>
                          )}
                        </FormControl>
                      </Grid>

                      <Grid item xs={12} md={3}>
                        <FormControl fullWidth variant="outlined">
                          <InputLabel>وضعیت</InputLabel>
                          <Select
                            name="status"
                            label="وضعیت"
                            value={values.status}
                            onChange={wrapFormikHandleChange(handleChange)}
                          >
                            <MenuItem value={1}>پیش‌نویس</MenuItem>
                            <MenuItem value={2}>فعال</MenuItem>
                            <MenuItem value={3}>غیرفعال</MenuItem>
                            <MenuItem value={4}>در حال بررسی</MenuItem>
                            <MenuItem value={5}>رد شده</MenuItem>
                            <MenuItem value={6}>بایگانی</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>

                      <Grid item xs={12} md={3}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={values.isActive}
                              onChange={(e) =>
                                setFieldValue("isActive", e.target.checked)
                              }
                              name="isActive"
                            />
                          }
                          label="فعال"
                        />
                      </Grid>

                      <Grid item xs={12} md={3}>
                        <TextField
                          label="قیمت با تجهیزات (ریال)"
                          type="number"
                          onChange={wrapFormikHandleChange(handleChange)}
                          {...onMakeFormikVariable("priceWithEquipment")}
                        />
                      </Grid>

                      <Grid item xs={12} md={3}>
                        <TextField
                          label="قیمت بدون تجهیزات (ریال)"
                          type="number"
                          onChange={wrapFormikHandleChange(handleChange)}
                          {...onMakeFormikVariable("priceWithoutEquipment")}
                        />
                      </Grid>

                      <Grid item xs={12} md={3}>
                        <TextField
                          label="هزینه ارسال (ریال)"
                          type="number"
                          onChange={wrapFormikHandleChange(handleChange)}
                          {...onMakeFormikVariable("deliveryFee")}
                        />
                      </Grid>

                      <Grid item xs={12} md={3}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={values.contractorPaysDelivery}
                              onChange={(e) =>
                                setFieldValue(
                                  "contractorPaysDelivery",
                                  e.target.checked
                                )
                              }
                              name="contractorPaysDelivery"
                            />
                          }
                          label="پیمانکار پرداخت ارسال"
                        />
                      </Grid>

                      <Grid item xs={12} md={3}>
                        <TextField
                          label="مدت اجرا (روز)"
                          type="number"
                          onChange={wrapFormikHandleChange(handleChange)}
                          {...onMakeFormikVariable("installationDurationDays")}
                        />
                      </Grid>

                      <Grid item xs={12} md={3}>
                        <TextField
                          label="مدت گارانتی (ماه)"
                          type="number"
                          onChange={wrapFormikHandleChange(handleChange)}
                          {...onMakeFormikVariable("warrantyMonths")}
                        />
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <TextField
                          label="توضیحات"
                          multiline
                          rows={4}
                          onChange={wrapFormikHandleChange(handleChange)}
                          {...onMakeFormikVariable("description")}
                        />
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <FileUploader
                          moduleName={"SLN/Supplier/PackageManagement"}
                          maxFiles={1}
                          label="تصویر پکیج"
                          accept="image/*"
                          baseUrl={baseUrl}
                          onChange={(value) => {
                            console.log(
                              "🖼️ FileUploader onChange value:",
                              value,
                              typeof value
                            );
                            const finalValue =
                              typeof value === "string" ? value : "";
                            console.log("✅ Setting imageUrl to:", finalValue);
                            setFieldValue("imageUrl", finalValue);
                          }}
                          placeholder="تصویر پکیج را انتخاب کنید"
                          {...onMakeFormikVariable("imageUrl")}
                        />
                      </Grid>
                      {/* تجهیزات */}
                      <Grid item xs={12}>
                        <Box
                          display="flex"
                          justifyContent="space-between"
                          alignItems="center"
                          mt={2}
                        >
                          <Typography variant="h6">تجهیزات پکیج</Typography>
                          <Button
                            variant="outlined"
                            startIcon={<AddIcon />}
                            onClick={() =>
                              setFieldValue("equipments", [
                                ...values.equipments,
                                {
                                  equipmentType: "",
                                  brand: "",
                                  model: "",
                                  quantity: 1,
                                  unitPrice: 0,
                                  totalPrice: 0,
                                  warrantyPeriod: "",
                                  description: "",
                                  imageUrl: "",
                                  datasheetUrl: "",
                                  equipmentItemId: null,
                                  publicCode: "",
                                  masterPicker: null,
                                },
                              ])
                            }
                          >
                            افزودن تجهیز
                          </Button>
                        </Box>
                      </Grid>

                      {values.equipments?.map((equipment, index) => (
                        <Grid item xs={12} key={index}>
                          <Card variant="outlined" sx={{ p: 2 }}>
                            <Grid container spacing={2} alignItems="center">
                              <Grid item xs={12}>
                                <Box
                                  display="flex"
                                  justifyContent="space-between"
                                  alignItems="center"
                                >
                                  <Typography variant="subtitle1">
                                    تجهیز {index + 1}
                                  </Typography>
                                  <IconButton
                                    color="error"
                                    onClick={() => {
                                      const newEquipments =
                                        values.equipments.filter(
                                          (_, i) => i !== index
                                        );
                                      setFieldValue(
                                        "equipments",
                                        newEquipments
                                      );
                                    }}
                                  >
                                    <DeleteIcon />
                                  </IconButton>
                                </Box>
                              </Grid>

                              <Grid item xs={12} md={6}>
                                <EquipmentItemPicker
                                  label="انتخاب از کاتالوگ مرجع (اختیاری)"
                                  value={equipment.masterPicker}
                                  onChange={(picked) => {
                                    const newEquipments = [...values.equipments];
                                    newEquipments[index].masterPicker = picked;
                                    newEquipments[index].equipmentItemId =
                                      picked?.equipmentItemId || null;
                                    newEquipments[index].publicCode =
                                      picked?.publicCode || "";
                                    if (picked?.brandName)
                                      newEquipments[index].brand = picked.brandName;
                                    if (picked?.modelName)
                                      newEquipments[index].model = picked.modelName;
                                    setFieldValue("equipments", newEquipments);
                                  }}
                                />
                                {equipment.publicCode ? (
                                  <Box sx={{ mt: 1 }}>
                                    <EquipmentPublicCodeChip code={equipment.publicCode} />
                                  </Box>
                                ) : null}
                              </Grid>

                              <Grid item xs={12} md={3}>
                                <FormControl fullWidth variant="outlined">
                                  <InputLabel>نوع تجهیز</InputLabel>
                                  <Select
                                    value={equipment.equipmentType}
                                    label="نوع تجهیز"
                                    onChange={(e) => {
                                      const newEquipments = [
                                        ...values.equipments,
                                      ];
                                      newEquipments[index].equipmentType =
                                        e.target.value;
                                      setFieldValue(
                                        "equipments",
                                        newEquipments
                                      );
                                    }}
                                  >
                                    {equipmentTypes.map((type) => (
                                      <MenuItem
                                        key={type.value}
                                        value={type.value}
                                      >
                                        {type.label || type.name}
                                      </MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>
                              </Grid>

                              <Grid item xs={12} md={3}>
                                <TextField
                                  fullWidth
                                  label="برند"
                                  value={equipment.brand}
                                  onChange={(e) => {
                                    const newEquipments = [
                                      ...values.equipments,
                                    ];
                                    newEquipments[index].brand = e.target.value;
                                    setFieldValue("equipments", newEquipments);
                                  }}
                                />
                              </Grid>

                              <Grid item xs={12} md={3}>
                                <TextField
                                  fullWidth
                                  label="مدل"
                                  value={equipment.model}
                                  onChange={(e) => {
                                    const newEquipments = [
                                      ...values.equipments,
                                    ];
                                    newEquipments[index].model = e.target.value;
                                    setFieldValue("equipments", newEquipments);
                                  }}
                                />
                              </Grid>

                              <Grid item xs={12} md={3}>
                                <TextField
                                  fullWidth
                                  label="تعداد"
                                  type="number"
                                  value={equipment.quantity}
                                  onChange={(e) => {
                                    const newEquipments = [
                                      ...values.equipments,
                                    ];
                                    const quantity =
                                      parseInt(e.target.value) || 0;
                                    newEquipments[index].quantity = quantity;
                                    newEquipments[index].totalPrice =
                                      quantity *
                                      (newEquipments[index].unitPrice || 0);
                                    setFieldValue("equipments", newEquipments);
                                  }}
                                />
                              </Grid>

                              <Grid item xs={12} md={3}>
                                <TextField
                                  fullWidth
                                  label="قیمت واحد (ریال)"
                                  type="number"
                                  value={equipment.unitPrice}
                                  onChange={(e) => {
                                    const newEquipments = [
                                      ...values.equipments,
                                    ];
                                    const unitPrice =
                                      parseFloat(e.target.value) || 0;
                                    newEquipments[index].unitPrice = unitPrice;
                                    newEquipments[index].totalPrice =
                                      unitPrice *
                                      (newEquipments[index].quantity || 0);
                                    setFieldValue("equipments", newEquipments);
                                  }}
                                />
                              </Grid>

                              <Grid item xs={12} md={3}>
                                <TextField
                                  fullWidth
                                  label="قیمت کل (ریال)"
                                  type="number"
                                  value={equipment.totalPrice}
                                  InputProps={{ readOnly: true }}
                                />
                              </Grid>

                              <Grid item xs={12} md={3}>
                                <TextField
                                  fullWidth
                                  label="گارانتی (ماه)"
                                  type="number"
                                  value={equipment.warrantyPeriod}
                                  onChange={(e) => {
                                    const newEquipments = [
                                      ...values.equipments,
                                    ];
                                    newEquipments[index].warrantyPeriod =
                                      e.target.value;
                                    setFieldValue("equipments", newEquipments);
                                  }}
                                />
                              </Grid>

                              <Grid item xs={12}>
                                <TextField
                                  fullWidth
                                  label="توضیحات و مشخصات فنی"
                                  multiline
                                  rows={4}
                                  value={equipment.description}
                                  onChange={(e) => {
                                    const newEquipments = [
                                      ...values.equipments,
                                    ];
                                    newEquipments[index].description =
                                      e.target.value;
                                    setFieldValue("equipments", newEquipments);
                                  }}
                                  placeholder="مثال: پنل خورشیدی مونو کریستال با توان 550 وات، بازدهی 21.2 درصد، ولتاژ مدار باز 49.5 ولت، دارای گارانتی 25 ساله بازدهی"
                                />
                              </Grid>

                              <Grid item xs={12}>
                                <FileUploader
                                  moduleName={"SLN/Supplier/PackageManagement"}
                                  maxFiles={1}
                                  label="تصویر تجهیز"
                                  accept="image/*"
                                  baseUrl={baseUrl}
                                  onChange={(value) => {
                                    const newEquipments = [
                                      ...values.equipments,
                                    ];
                                    newEquipments[index].imageUrl =
                                      typeof value === "string" ? value : "";
                                    setFieldValue("equipments", newEquipments);
                                  }}
                                  placeholder="تصویر تجهیز را انتخاب کنید"
                                  value={equipment.imageUrl}
                                />
                              </Grid>
                            </Grid>
                          </Card>
                        </Grid>
                      ))}

                      {/* ویژگی‌ها */}
                      <Grid item xs={12}>
                        <Box
                          display="flex"
                          justifyContent="space-between"
                          alignItems="center"
                          mt={2}
                        >
                          <Typography variant="h6">ویژگی‌های پکیج</Typography>
                          <Button
                            variant="outlined"
                            startIcon={<AddIcon />}
                            onClick={() =>
                              setFieldValue("features", [
                                ...values.features,
                                {
                                  featureTitle: "",
                                  featureValue: "",
                                  description: "",
                                  displayOrder: values.features.length + 1,
                                },
                              ])
                            }
                          >
                            افزودن ویژگی
                          </Button>
                        </Box>
                      </Grid>

                      {values.features?.map((feature, index) => (
                        <Grid item xs={12} key={index}>
                          <Card variant="outlined" sx={{ p: 2 }}>
                            <Grid container spacing={2} alignItems="center">
                              <Grid item xs={12}>
                                <Box
                                  display="flex"
                                  justifyContent="space-between"
                                  alignItems="center"
                                >
                                  <Typography variant="subtitle1">
                                    ویژگی {index + 1}
                                  </Typography>
                                  <IconButton
                                    color="error"
                                    onClick={() => {
                                      const newFeatures =
                                        values.features.filter(
                                          (_, i) => i !== index
                                        );
                                      setFieldValue("features", newFeatures);
                                    }}
                                  >
                                    <DeleteIcon />
                                  </IconButton>
                                </Box>
                              </Grid>

                              <Grid item xs={12} md={3}>
                                <TextField
                                  fullWidth
                                  label="عنوان ویژگی"
                                  value={feature.featureTitle}
                                  onChange={(e) => {
                                    const newFeatures = [...values.features];
                                    newFeatures[index].featureTitle =
                                      e.target.value;
                                    setFieldValue("features", newFeatures);
                                  }}
                                />
                              </Grid>

                              <Grid item xs={12} md={4}>
                                <TextField
                                  fullWidth
                                  label="مقدار ویژگی"
                                  value={feature.featureValue}
                                  onChange={(e) => {
                                    const newFeatures = [...values.features];
                                    newFeatures[index].featureValue =
                                      e.target.value;
                                    setFieldValue("features", newFeatures);
                                  }}
                                />
                              </Grid>

                              <Grid item xs={12} md={2}>
                                <TextField
                                  fullWidth
                                  label="ترتیب"
                                  type="number"
                                  value={feature.displayOrder}
                                  onChange={(e) => {
                                    const newFeatures = [...values.features];
                                    newFeatures[index].displayOrder =
                                      parseInt(e.target.value) || 0;
                                    setFieldValue("features", newFeatures);
                                  }}
                                />
                              </Grid>

                              <Grid item xs={12}>
                                <TextField
                                  fullWidth
                                  label="توضیحات ویژگی"
                                  multiline
                                  rows={2}
                                  value={feature.description}
                                  onChange={(e) => {
                                    const newFeatures = [...values.features];
                                    newFeatures[index].description =
                                      e.target.value;
                                    setFieldValue("features", newFeatures);
                                  }}
                                />
                              </Grid>
                            </Grid>
                          </Card>
                        </Grid>
                      ))}

                      {/* دکمه‌ها */}
                      <Grid
                        item
                        xs={12}
                        container
                        justifyContent="flex-end"
                        spacing={2}
                        mt={2}
                      >
                        <Grid item>
                          <Button
                            disabled={isSubmitting || createUpdateLoading}
                            variant="outlined"
                            size="large"
                            onClick={onNavigateToListPage}
                          >
                            انصراف
                          </Button>
                        </Grid>
                        <Grid item>
                          <Button
                            disabled={isSubmitting || createUpdateLoading}
                            variant="contained"
                            type="submit"
                            size="large"
                          >
                            {createUpdateLoading && (
                              <CircularProgress
                                size={16}
                                color="inherit"
                                sx={{ mr: 1 }}
                              />
                            )}
                            {isEditMode ? "ویرایش پکیج" : "ایجاد پکیج"}
                          </Button>
                        </Grid>
                      </Grid>
                    </Grid>
                  </form>
                );
              }}
            </Formik>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};

export default PackageModifyPage;
